import { html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { BulletColor } from '../types';
import { FontSizes } from '../../../types/common/types';

/**
 * @summary This component it's used to display a bullet with a color and a text
 * The color can be set using predefined colors or a custom color, by setting the color attribute
 * @tag GzBullet
 */
@customElement('gz-bullet')
export class GzBulletWebComponent extends LitElement {
  @property({ type: String, attribute: true, reflect: true })
  color: BulletColor;

  /**
   * The size of the box in which the bullet sits. This may be undefined, so
   * the box and bullet will be the same size.
   */
  @property({ type: Number, attribute: true, reflect: true })
  boxSize?: FontSizes = undefined;

  /**
   * The size of the bullet itself.
   */
  @property({ type: Number, attribute: true, reflect: true })
  size: FontSizes = FontSizes.PARAGRAPH_10;

  /**
   * By default, the bullet has some margin to the right to allow for text next to it.
   * This may be used to center the bullet in its box and disable the margin.
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  center = false;

  /**
   * Reverse the order of the bullet and the internal slot.
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  reverse = false;

  @state()
  private colors: Map<BulletColor, string> = new Map<BulletColor, string>([
    [BulletColor.NEUTRAL, 'var(--chart-300-icon-buttons)'],
    [BulletColor.SUCCESS, 'var(--green-layout-100-primary-green)'],
    [BulletColor.WARNING, 'var(--orange-layout-050-warning)'],
    [BulletColor.DANGER, 'var(--red-alert-500-severe)'],
  ]);

  static get styles() {
    return styles;
  }

  render() {
    const boxStyle = this.boxSize ? `width: ${this.boxSize}px; height: ${this.boxSize}px` : '';

    return html`
      <div
        style="${boxStyle}"
        class="has-bullet ${this.color}">
        <span
          class="bullet"
          style="
        background-color: ${this.colors.get(this.color) || this.color};
        width: ${this.size}px;
        height: ${this.size}px;
        "
        ></span>
        <slot></slot>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-bullet': GzBulletWebComponent;
  }
}
